import { useState, useCallback } from "react";
import {
  Box,
  BoxProps,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import LoadingButton from "@mui/lab/LoadingButton";
import { uniq, orderBy } from "lodash";

import { IssueStatus, MonitoringIssue } from "../../fixtures";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { muteIssue } from "./mutations";

export interface Props extends BoxProps {
  issue: MonitoringIssue;
  disabled?: boolean;
}

export default function MuteButton({ issue, disabled = false, ...etc }: Props) {
  const queryClient = useQueryClient();

  const [el, setEl] = useState<HTMLElement | null>(null);
  const [selectedMuteAction, setSelectedMuteAction] = useState("");

  const muteMutation = useMutation({
    mutationFn: () => muteIssue(issue.id),
    mutationKey: ["issues", "mute", issue.id],
    onSuccess: () => {
      setEl(null);
      setSelectedMuteAction("");

      queryClient.invalidateQueries({
        queryKey: ["issueActivity", issue.id],
      });

      queryClient.invalidateQueries({
        queryKey: ["getIssue", issue.id],
      });

      queryClient.invalidateQueries({
        queryKey: ["my", "issues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["all", "issues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["getMonitorIssues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["my", "issues", "openIssueCount"],
      });

      queryClient.invalidateQueries({
        queryKey: ["all", "issues", "openIssueCount"],
      });

      // TODO: optimistic updates
    },
    onError: (error, variables, context) => {},
  });

  const onCancel = useCallback(() => {
    setEl(null);
  }, []);

  const alertTypes = [
    ...new Set(
      issue.alerts
        .map((alert) => alert.type)
        .flat()
        .sort(),
    ),
  ];

  return (
    <Box {...etc}>
      <IconButton
        sx={{
          display: "flex",
          flexShrink: 0,
        }}
        disabled={
          disabled ||
          issue.status === IssueStatus.Resolved ||
          issue.status === IssueStatus.Muted
        }
        onClick={(e) => {
          setEl(e.currentTarget);
        }}
      >
        <RemoveCircleIcon />
      </IconButton>
      <Popover
        open={!disabled && el != null}
        anchorEl={el}
        onClose={muteMutation.isPending ? undefined : onCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack sx={{ padding: "1em", width: "300px" }}>
          <FormControl>
            <FormLabel>Mute future {issue.domain} alerts</FormLabel>
            <RadioGroup
              value={selectedMuteAction}
              onChange={(e) => {
                setSelectedMuteAction(e.target.value);
              }}
            >
              <FormControlLabel
                value="3"
                control={<Radio />}
                label={`all from ${issue.domain}`}
              />
              {alertTypes.map((contentType) => (
                <FormControlLabel
                  value={`content-type-${contentType}`}
                  key={`content-type-${contentType}`}
                  control={<Radio />}
                  label={`any ${contentType}-related`}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <div
            style={{
              display: "flex",
              gap: "1em",
              justifyContent: "end",
              marginTop: "1em",
            }}
          >
            {/* TODO: error alert */}
            <Button
              variant="outlined"
              disabled={muteMutation.isPending}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={muteMutation.isPending}
              disabled={!selectedMuteAction}
              variant="contained"
              onClick={async () => {
                await muteMutation.mutateAsync();
              }}
            >
              Mute
            </LoadingButton>
          </div>
        </Stack>
      </Popover>
    </Box>
  );
}
